import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';

// Add Google Fonts link to head
const link = document.createElement('link');
link.rel = 'stylesheet';
link.href = 'https://fonts.googleapis.com/css2?family=Albert+Sans:wght@100;200;300;400;500;600;700;800;900&display=swap';
document.head.appendChild(link);


const NavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 1000;
  border-bottom: 1px solid #eee;
  height: 90px;
`;

const Nav = styled.nav`
  width: 100%;
  height: 60px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Logo = styled(Link)`
  text-decoration: none;
  color: black;
  z-index: 1001;
  font-family: 'Albert Sans', sans-serif;
  font-weight: 300 !important;
  font-size: 20px;
  letter-spacing: 3px;
`;

const LogoImage = styled.img`
  height: 36px;
  display: block;

  @media (max-width: 960px) {
    height: 32px;
  }
`;

const LeftNav = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const RightNav = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1001;
  font-size: 24px;
  
  @media (max-width: 960px) {
    display: block;
  }
`;

const FullscreenMenu = styled.div`
  display: none;
  
  @media (max-width: 960px) {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: #fff;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    opacity: ${props => props.$isOpen ? 1 : 0};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
    transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
    transition: opacity 0.35s ease, transform 0.35s ease, visibility 0.35s ease;
    padding: 24px;
    overflow-y: auto;
  }
`;

const MobileMenuItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 22px;
  font-weight: 200;
  padding: 5px;
  text-align: center;
  width: 100%;

  & > * {
    opacity: ${props => props.$isOpen ? 1 : 0};
    transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(8px)'};
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  & > *:nth-child(1) { transition-delay: 0.05s; }
  & > *:nth-child(2) { transition-delay: 0.08s; }
  & > *:nth-child(3) { transition-delay: 0.11s; }
  & > *:nth-child(4) { transition-delay: 0.14s; }
  & > *:nth-child(5) { transition-delay: 0.17s; }
  & > *:nth-child(6) { transition-delay: 0.2s; }
  & > *:nth-child(7) { transition-delay: 0.23s; }
  & > *:nth-child(8) { transition-delay: 0.26s; }
  & > *:nth-child(9) { transition-delay: 0.29s; }
  & > *:nth-child(10) { transition-delay: 0.32s; }
  & > *:nth-child(11) { transition-delay: 0.35s; }
  & > *:nth-child(12) { transition-delay: 0.38s; }
`;

const MobileMenuItem = styled(Link)`
  text-decoration: none;
  color: black;
  font-size: 22px;
  font-weight: 200;
  position: relative;
  padding: 5px;
  text-align: center;
  width: 100%;
  
  &:hover {
    text-decoration: underline;
  }
`;

const MobileLogo = styled.div`
  margin-bottom: 20px;
  font-family: 'Albert Sans', sans-serif;
  font-weight: 300 !important;
  font-size: 22px;
  letter-spacing: 3px;
`;

const SocialIcon = styled.a`
  color: black;
  text-decoration: none;
  font-size: 18px;
  margin: 0 8px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const DesktopSocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 960px) {
    display: none;
  }
`;

const MenuItems = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  
  @media (max-width: 960px) {
    display: none;
  }
`;

const MenuItem = styled.div`
  position: relative;
  cursor: pointer;
  padding: 8px 0;
  width: 80px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Dropdown = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  margin-top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  min-width: 200px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  padding: 0.5rem 0;
  z-index: 1000;
  
  ${MenuItem}:hover & {
    display: block;
  }

  ${MenuItem}:hover &::before {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.5rem 2rem;
  text-decoration: none;
  color: black;
  white-space: nowrap;
  border-bottom: 1px solid #eee;
  position: relative;
  
  span {
    display: inline-block;
    transform: translateX(0);
    transition: transform 0.2s ease;
  }
  
  &:hover {
    background: #f5f5f5;

    span {
      transform: translateX(20px);
    }

    &:before {
      content: ">";
      position: absolute;
      left: 1rem;
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:before {
    content: ">";
    position: absolute;
    left: 1rem;
    opacity: 0;
    transform: translateY(5px);
    transition: all 0.2s ease;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const MenuLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: black;
  font-weight: 400;
  &:hover {
    text-decoration: underline;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: #000;
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  border: 1px solid #000;

  &:hover {
    opacity: 0.85;
  }
`;

const NewsLabel = styled.span`
  cursor: default;
  color: black;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &::after {
    content: '▾';
    margin-left: 6px;
    font-size: 12px;
  }
`;

const MobileSectionLabel = styled.span`
  font-size: 22px;
  font-weight: 200;
  padding: 6px 5px;
  color: black;
`;

const MobileSocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  transition-delay: 0.4s;
`;
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      <NavWrapper>
        <Nav>
        <LeftNav>
          <Logo to="/" $isMenuOpen={isMenuOpen}>
            <LogoImage src="/img/logo/sleepr-studio-logo.png" alt="Sleepr Studio" />
          </Logo>
          
          <MenuItems>
            <MenuItem>
              <PrimaryButton href="https://shop.sleeprstudio.com" target="_blank" rel="noreferrer">
                Shop
              </PrimaryButton>
            </MenuItem>
            <MenuItem>
              <NewsLabel>News</NewsLabel>
              <Dropdown>
                <DropdownItem to="/news/shop-opening">
                  <span>Shop Opening</span>
                </DropdownItem>
                <DropdownItem to="/news/maps-of-hyperspace-documentary">
                  <span>Maps Of Hyperspace Documentary</span>
                </DropdownItem>
                <DropdownItem to="/news/fabuk-magazine-london-exhibition-review">
                  <span>FabUK Magazine Review</span>
                </DropdownItem>
                <DropdownItem to="/news/icva-partnership-2025">
                  <span>ICVA Partnership 2025</span>
                </DropdownItem>
                <DropdownItem to="/news/the-amazon-peru-2025">
                  <span>Residency In The Amazon: Peru 2025</span>
                </DropdownItem>
                <DropdownItem to="/news/solo-exhibition-london-2024">
                  <span>Solo Exhibition: London, 2024</span>
                </DropdownItem>
                <DropdownItem to="/news/art-basel-miami-2023">
                  <span>Art Basel Miami 2023</span>
                </DropdownItem>
                <DropdownItem to="/news/sothebys-hong-kong-2023">
                  <span>Sotheby's Hong Kong 2023</span>
                </DropdownItem>
              </Dropdown>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/art">Artworks</MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink to="/about">About</MenuLink>
            </MenuItem>
          </MenuItems>
        </LeftNav>

        <RightNav>
          <DesktopSocialIcons>
            <SocialIcon href="https://www.instagram.com/sleeprarts/" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faInstagram} />
            </SocialIcon>
            <SocialIcon href="https://x.com/sleeprarts" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faXTwitter} />
            </SocialIcon>
            <SocialIcon href="https://www.youtube.com/@sleeprarts" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faYoutube} />
            </SocialIcon>
          </DesktopSocialIcons>

          <HamburgerButton onClick={toggleMenu}>
          {isMenuOpen ? (
            <i className="fas fa-times"></i>
          ) : (
            <i className="fas fa-bars"></i>
          )}
          </HamburgerButton>
        </RightNav>
       </Nav>
        <FullscreenMenu $isOpen={isMenuOpen}>
          <MobileLogo>
            SLEEPR
          </MobileLogo>
          
          <MobileMenuItems $isOpen={isMenuOpen}>
            <PrimaryButton href="https://shop.sleeprstudio.com" target="_blank" rel="noreferrer">
              Shop
            </PrimaryButton>
            <MobileSectionLabel>News</MobileSectionLabel>
            <MobileMenuItem to="/news/shop-opening" onClick={closeMenu}>Shop Opening</MobileMenuItem>
            <MobileMenuItem to="/news/maps-of-hyperspace-documentary" onClick={closeMenu}>Maps Of Hyperspace Documentary</MobileMenuItem>
            <MobileMenuItem to="/news/fabuk-magazine-london-exhibition-review" onClick={closeMenu}>FabUK Magazine Review</MobileMenuItem>
            <MobileMenuItem to="/news/icva-partnership-2025" onClick={closeMenu}>ICVA Partnership 2025</MobileMenuItem>
            <MobileMenuItem to="/news/the-amazon-peru-2025" onClick={closeMenu}>Residency In The Amazon: Peru 2025</MobileMenuItem>
            <MobileMenuItem to="/news/solo-exhibition-london-2024" onClick={closeMenu}>Solo Exhibition: London, 2024</MobileMenuItem>
            <MobileMenuItem to="/news/art-basel-miami-2023" onClick={closeMenu}>Art Basel Miami 2023</MobileMenuItem>
            <MobileMenuItem to="/news/sothebys-hong-kong-2023" onClick={closeMenu}>Sotheby's Hong Kong 2023</MobileMenuItem>
            <MobileMenuItem to="/art" onClick={closeMenu}>Artworks</MobileMenuItem>
            <MobileMenuItem to="/about" onClick={closeMenu}>About</MobileMenuItem>
          </MobileMenuItems>
          <MobileSocialIcons $isOpen={isMenuOpen}>
            <SocialIcon href="https://www.instagram.com/sleeprarts/" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faInstagram} />
            </SocialIcon>
            <SocialIcon href="https://x.com/sleeprarts" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faXTwitter} />
            </SocialIcon>
            <SocialIcon href="https://www.youtube.com/@sleeprarts" target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faYoutube} />
            </SocialIcon>
          </MobileSocialIcons>
        </FullscreenMenu>
     </NavWrapper>
     </>
   );
 };

export default Navbar; 
export const NAVBAR_HEIGHT = '80px';